import "./AppsMenu.scss";
import { useId, useState, type FC } from "react";
import { Link, useLocation } from "react-router-dom";
import { SeemPopover } from "@SeemplicityDev/shared";
import {
  isAppsMenuActive,
  isAppsMenuParent,
  type AppsMenuEntry,
  type AppsMenuParent,
} from "./appsMenuConfig";
import NavIconGlyph from "../NavIconGlyph";

interface AppsMenuProps {
  entries: AppsMenuEntry[];
}

const AppsMenu: FC<AppsMenuProps> = ({ entries }) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [openParentId, setOpenParentId] = useState<string | null>(null);
  const panelId = useId();

  const close = () => {
    setIsOpen(false);
    setOpenParentId(null);
  };

  const toggle = () => {
    if (isOpen) {
      close();
    } else {
      setIsOpen(true);
    }
  };

  // The trigger wrapper calls this with no argument on click; toggle owns that.
  const handleOpenChange = (open?: boolean) => {
    if (open === false) {
      close();
    }
  };

  const openParent = entries.find(
    (entry): entry is AppsMenuParent =>
      isAppsMenuParent(entry) && entry.id === openParentId
  );

  const isActive = isOpen || isAppsMenuActive(pathname, entries);

  const rootView = entries.map((entry) =>
    isAppsMenuParent(entry) ? (
      <button
        key={entry.id}
        type="button"
        className="apps-menu-row"
        data-testid={`apps-menu-item-${entry.id}`}
        onClick={() => setOpenParentId(entry.id)}
      >
        <NavIconGlyph icon={entry.icon} />
        <span className="apps-menu-row-label sub-menu-item-title">
          {entry.label}
        </span>
        <i
          className="apps-menu-row-chevron seem-icon seem-icon-right-arrow"
          aria-hidden="true"
        />
      </button>
    ) : (
      <Link
        key={entry.id}
        to={entry.to}
        className="apps-menu-row"
        data-testid={`apps-menu-item-${entry.id}`}
        onClick={close}
      >
        {!!entry.icon && <NavIconGlyph icon={entry.icon} />}
        <span className="apps-menu-row-label sub-menu-item-title">
          {entry.label}
        </span>
      </Link>
    )
  );

  const parentView = openParent && (
    <>
      <button
        type="button"
        className="apps-menu-back"
        data-testid="apps-menu-back"
        onClick={() => setOpenParentId(null)}
      >
        <i className="seem-icon seem-icon-left-arrow" aria-hidden="true" />
        <span>{openParent.label}</span>
      </button>
      {openParent.children.map((child) => (
        <Link
          key={child.id}
          to={child.to}
          className="apps-menu-row"
          data-testid={`sub-menu-item-${child.id}`}
          onClick={close}
        >
          {!!child.icon && <NavIconGlyph icon={child.icon} />}
          <span className="apps-menu-row-label sub-menu-item-title">
            {child.label}
          </span>
        </Link>
      ))}
    </>
  );

  return (
    <SeemPopover
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      placement="bottom-end"
      className="apps-menu-popover"
      onOuterAction={close}
      body={
        isOpen && (
          <nav
            id={panelId}
            className="apps-menu"
            aria-label="Apps"
            data-testid="apps-menu"
          >
            {parentView || rootView}
          </nav>
        )
      }
    >
      <button
        type="button"
        className={
          isActive ? "apps-menu-trigger is-active" : "apps-menu-trigger"
        }
        aria-label="Apps"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        data-testid="apps-menu-button"
        onClick={toggle}
      >
        <i className="seem-icon seem-icon-apps" aria-hidden="true" />
      </button>
    </SeemPopover>
  );
};

export default AppsMenu;
